const Cast = require('../util/cast.js');
const MathUtil = require('../util/math-util.js');

const isMicrocontrollerTarget = (util) => {
    if (!util || !util.thread || !util.thread.blockContainer) return false;
    const blocks = util.thread.blockContainer._blocks;
    if (!blocks) return false;
    for (const blockId in blocks) {
        const opcode = blocks[blockId].opcode;
        if (!opcode) continue;
        if (
            opcode.startsWith('stemengine') ||
            opcode.startsWith('esp32') ||
            opcode.startsWith('arduino') ||
            opcode.startsWith('car') ||
            opcode.startsWith('stemsensors') ||
            opcode.startsWith('stemactuators') ||
            opcode.startsWith('sensors') ||
            opcode.startsWith('display_modules') ||
            opcode.startsWith('actuators') ||
            opcode.startsWith('pythonRobo') ||
            opcode.startsWith('robotic') ||
            opcode.startsWith('humanoid')
        ) {
            return true;
        }
    }
    return false;
};

const isCodeGenMode = (util) => {
    const isCodeGen = typeof localStorage !== 'undefined' &&
        (localStorage.getItem('codeContext') === 'setup' || localStorage.getItem('codeContext') === 'loop');
    if (!isCodeGen) return false;
    if (!util) return true;
    return isMicrocontrollerTarget(util);
};

class Scratch3OperatorsBlocks {
    constructor(runtime) {
        /**
         * The runtime instantiating this block package.
         * @type {Runtime}
         */
        this.runtime = runtime;
    }

    /**
     * Retrieve the block primitives implemented by this package.
     * @return {object.<string, Function>} Mapping of opcode to Function.
     */
    getPrimitives() {
        return {
            operator_add: this.add,
            operator_subtract: this.subtract,
            operator_multiply: this.multiply,
            operator_divide: this.divide,
            operator_lt: this.lt,
            operator_equals: this.equals,
            operator_gt: this.gt,
            operator_and: this.and,
            operator_or: this.or,
            operator_not: this.not,
            operator_random: this.random,
            operator_join: this.join,
            operator_letter_of: this.letterOf,
            operator_length: this.length,
            operator_contains: this.contains,
            operator_mod: this.mod,
            operator_round: this.round,
            operator_mathop: this.mathop
        };
    }

    add(args, util) {
        if (isCodeGenMode(util)) {
            return `${args.NUM1} + ${args.NUM2}`;
        }
        return Cast.toNumber(args.NUM1) + Cast.toNumber(args.NUM2);
    }

    subtract(args, util) {
        if (isCodeGenMode(util)) {
            return `${args.NUM1} - ${args.NUM2}`;
        }
        return Cast.toNumber(args.NUM1) - Cast.toNumber(args.NUM2);
    }

    multiply(args, util) {
        if (isCodeGenMode(util)) {
            return `${args.NUM1} * ${args.NUM2}`;
        }
        return Cast.toNumber(args.NUM1) * Cast.toNumber(args.NUM2);
    }

    divide(args, util) {
        if (isCodeGenMode(util)) {
            return `${args.NUM1} / ${args.NUM2}`;
        }
        return Cast.toNumber(args.NUM1) / Cast.toNumber(args.NUM2);
    }

    lt(args, util) {
        if (isCodeGenMode(util)) {
            return `${args.OPERAND1} < ${args.OPERAND2}`;
        }
        return Cast.compare(args.OPERAND1, args.OPERAND2) < 0;
    }

    equals(args, util) {
        if (isCodeGenMode(util)) {
            return `${args.OPERAND1} == ${args.OPERAND2}`;
        }
        return Cast.compare(args.OPERAND1, args.OPERAND2) === 0;
    }

    gt(args, util) {
        if (isCodeGenMode(util)) {
            return `${args.OPERAND1} > ${args.OPERAND2}`;
        }
        return Cast.compare(args.OPERAND1, args.OPERAND2) > 0;
    }

    and(args, util) {
        if (isCodeGenMode(util)) {
            return `(${args.OPERAND1}) && (${args.OPERAND2})`;
        }
        return Cast.toBoolean(args.OPERAND1) && Cast.toBoolean(args.OPERAND2);
    }

    or(args, util) {
        if (isCodeGenMode(util)) {
            return `(${args.OPERAND1}) || (${args.OPERAND2})`;
        }
        return Cast.toBoolean(args.OPERAND1) || Cast.toBoolean(args.OPERAND2);
    }

    not(args, util) {
        if (isCodeGenMode(util)) {
            return `!(${args.OPERAND})`;
        }
        return !Cast.toBoolean(args.OPERAND);
    }

    random(args, util) {
        if (isCodeGenMode(util)) {
            return `random(${args.FROM}, ${args.TO})`;
        }
        const nFrom = Cast.toNumber(args.FROM);
        const nTo = Cast.toNumber(args.TO);
        const low = nFrom <= nTo ? nFrom : nTo;
        const high = nFrom <= nTo ? nTo : nFrom;
        if (low === high) return low;
        // If both arguments are ints, truncate the result to an int.
        if (Cast.isInt(args.FROM) && Cast.isInt(args.TO)) {
            return low + Math.floor(Math.random() * ((high + 1) - low));
        }
        return (Math.random() * (high - low)) + low;
    }

    join(args, util) {
        if (isCodeGenMode(util)) {
            return `String(${args.STRING1}) + String(${args.STRING2})`;
        }
        return Cast.toString(args.STRING1) + Cast.toString(args.STRING2);
    }

    letterOf(args, util) {
        if (isCodeGenMode(util)) {
            return `String(${args.STRING}).charAt(${args.LETTER} - 1)`;
        }
        const index = Cast.toNumber(args.LETTER) - 1;
        const str = Cast.toString(args.STRING);
        // Out of bounds?
        if (index < 0 || index >= str.length) {
            return '';
        }
        return str.charAt(index);
    }

    length(args, util) {
        if (isCodeGenMode(util)) {
            return `String(${args.STRING}).length()`;
        }
        return Cast.toString(args.STRING).length;
    }

    contains(args, util) {
        if (isCodeGenMode(util)) {
            return `String(${args.STRING1}).indexOf(String(${args.STRING2})) != -1`;
        }
        const format = function (string) {
            return Cast.toString(string).toLowerCase();
        };
        return format(args.STRING1).includes(format(args.STRING2));
    }

    mod(args, util) {
        if (isCodeGenMode(util)) {
            return `((int)${args.NUM1} % (int)${args.NUM2})`;
        }
        const n = Cast.toNumber(args.NUM1);
        const modulus = Cast.toNumber(args.NUM2);
        let result = n % modulus;
        // Scratch mod uses floored division instead of truncated division.
        if (result / modulus < 0) result += modulus;
        return result;
    }

    round(args, util) {
        if (isCodeGenMode(util)) {
            return `round(${args.NUM})`;
        }
        return Math.round(Cast.toNumber(args.NUM));
    }

    mathop(args, util) {
        if (isCodeGenMode(util)) {
            const operator = Cast.toString(args.OPERATOR).toLowerCase();
            const n = args.NUM;
            switch (operator) {
                case 'abs': return `abs(${n})`;
                case 'floor': return `floor(${n})`;
                case 'ceiling': return `ceil(${n})`;
                case 'sqrt': return `sqrt(${n})`;
                case 'sin': return `sin(${n} * DEG_TO_RAD)`;
                case 'cos': return `cos(${n} * DEG_TO_RAD)`;
                case 'tan': return `tan(${n} * DEG_TO_RAD)`;
                case 'asin': return `asin(${n}) * RAD_TO_DEG`;
                case 'acos': return `acos(${n}) * RAD_TO_DEG`;
                case 'atan': return `atan(${n}) * RAD_TO_DEG`;
                case 'ln': return `log(${n})`;
                case 'log': return `(log(${n}) / log(10))`;
                case 'e ^': return `exp(${n})`;
                case '10 ^': return `pow(10, ${n})`;
            }
            return '0';
        }
        const operator = Cast.toString(args.OPERATOR).toLowerCase();
        const n = Cast.toNumber(args.NUM);
        switch (operator) {
            case 'abs': return Math.abs(n);
            case 'floor': return Math.floor(n);
            case 'ceiling': return Math.ceil(n);
            case 'sqrt': return Math.sqrt(n);
            case 'sin': return parseFloat(Math.sin((Math.PI * n) / 180).toFixed(10));
            case 'cos': return parseFloat(Math.cos((Math.PI * n) / 180).toFixed(10));
            case 'tan': return MathUtil.tan(n);
            case 'asin': return (Math.asin(n) * 180) / Math.PI;
            case 'acos': return (Math.acos(n) * 180) / Math.PI;
            case 'atan': return (Math.atan(n) * 180) / Math.PI;
            case 'ln': return Math.log(n);
            case 'log': return Math.log(n) / Math.LN10;
            case 'e ^': return Math.exp(n);
            case '10 ^': return Math.pow(10, n);
        }
        return 0;
    }
}

module.exports = Scratch3OperatorsBlocks;
